import {useCallback, useEffect, useState} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { GetZoneTree } from '../../../../../reduxToolkit/EmployeeContractors/EmployeeContractorsApi';
import { getZonetree } from '../../../../../reduxToolkit/EmployeeContractors/EmployeeContractorsSlice';
import moment from 'moment';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import {ALL_TIME_TO, CUSTOMIZED_MODEL, DEFAULT_TIME} from '../Model/Models';
import {isToValueValid} from "../../WorkShift/Model/Model";

const Customized = ({ customizedList, setCustomizedList }) => {
    const dispatch = useDispatch();
    const zoneData = useSelector(getZonetree);
    const { t } = useTranslation();

    const [item, setItem] = useState({...CUSTOMIZED_MODEL});
    const [isValidTo, setIsValidTo] = useState(true);
    const [emptyFields, setEmptyFields] = useState(false);

    useEffect(() => {
        dispatch(GetZoneTree());
    }, []);

    function deepEqual(obj1, obj2) {
        if (obj1 === obj2) return true;

        if (
            typeof obj1 !== 'object' ||
            typeof obj2 !== 'object' ||
            obj1 === null ||
            obj2 === null
        ) {
            return false;
        }

        const keys1 = Object.keys(obj1).slice(0, 4);
        const keys2 = Object.keys(obj2).slice(0, 4);

        if (keys1.length !== keys2.length) return false;

        for (let key of keys1) {
            if (!keys2.includes(key) || !deepEqual(obj1[key], obj2[key])) return false;
        }

        return true;
    }

    const handleCheck = (event) => {
        const value = event.target.value;

        setItem((prev) => {
            let updatedList = [...prev.checkedZones];

            if (event.target.checked) {
                updatedList = [...updatedList, value];
            }
            else {
                updatedList = updatedList.filter((x) => x !== value);
            }

            return { ...prev, checkedZones: updatedList };
        });
    };

    const resetState = () => {
        setItem({...item.clear()})
        setEmptyFields(false);
        setIsValidTo(true);
    };

    const selectAllDays = (e) => {
        const checked = e.target.checked;

        setItem((prev) => ({
            ...prev,
            allDays: checked,
            dayArray: checked ? [1, 2, 3, 4, 5, 6, 0] : [],
        }));
    };

    const handleAllTime = (event) => {
        const checked = event.target.checked;

        setItem((prev) => ({
            ...prev,
            allTime: checked,
            fromValue: DEFAULT_TIME,
            toValue: checked ? ALL_TIME_TO : DEFAULT_TIME,
        }));
    };

    const handleCLick = async () => {
        if (!item.isValid() || !isValidTo) {
            if(!item.isValid()){
                toast.error(t('please_fill_all_fields'));
                setEmptyFields(true);
            }
            return;
        }

        setEmptyFields(false);
        setIsValidTo(true);
        let updateArray = [...customizedList];

        for (let i = 0; i < item.checkedZones.length; i++) {
            for (let j = 0; j < item.dayArray.length; j++) {
                const x = {
                    zoneId: item.checkedZones[i].split(',')[0],
                    zoneName :item.checkedZones[i].split(',')[1],
                    dayId: item.dayArray[j],
                    from: moment(item.fromValue).format('HH:mm:ss'),
                    to: moment(item.toValue).format('HH:mm:ss'),
                    added: false,
                    new: true,
                };

                if (!updateArray.some((item) => deepEqual(item, x))) {
                    updateArray.push(x);
                }
            }
        }

        setCustomizedList(updateArray);
        resetState();
    };

    const handleClose = () => {
        setCustomizedList([...customizedList.filter((e) => e.added)]);
        resetState();
    };

    const handleRemoveAccess = (index) => {
        setCustomizedList((prevArray) => prevArray.filter((item, i) => i !== index));
    };

    const handleConfirm = () => {
        setCustomizedList((prevArray) => prevArray.map((item) => ({ ...item, added: true })));
    };

    const handleAdded = () => {
        return customizedList.map((item) => ({
            ...item,
            added: true,
            new: true,
        }));
    };

    /**
     * IMPORTANT:
     * These setters MUST support:
     * - setX(newArray)
     * - setX(prev => newArray)
     * Because CustomTreeView and CustomDaySelector call them that way.
     */
    const setChecked = useCallback((valueOrFn) => {
        setItem((prev) => {
            const nextChecked =
                typeof valueOrFn === 'function'
                    ? valueOrFn(prev.checkedZones || [])
                    : valueOrFn;

            return { ...prev, checkedZones: nextChecked };
        });
    }, [setItem]);

    const setDayArray = useCallback((valueOrFn) => {
        setItem((prev) => {
            const nextDays =
                typeof valueOrFn === 'function'
                    ? valueOrFn(prev.dayArray || [])
                    : valueOrFn;

            return { ...prev, dayArray: nextDays };
        });
    }, [setItem]);

    useEffect(() => {
        if (!item?.fromValue || !item?.toValue){
            setIsValidTo(true);
            return;
        }
        setIsValidTo(isToValueValid(item.fromValue, item.toValue));
    }, [item?.fromValue, item?.toValue]);


    const onClick = async(e) => {
        if(!isValidTo) return;
        await handleCLick(e);
    }

    return {
        zoneData,
        item,
        setItem,
        handleCheck,
        selectAllDays,
        onClick,
        handleClose,
        handleAllTime,
        handleConfirm,
        handleRemoveAccess,
        handleAdded,
        emptyFields,
        setEmptyFields,
        setChecked,
        setDayArray,
        isValidTo
    };
};

export default Customized;
